/**
 * TermConsole.tsx
 *
 * Customer serial console at /term/:sessionId.
 *
 * Flow:
 *   1. Pull sessionId from the route.
 *   2. GET /api/vnc/session/:sessionId for { wsPath, pveUser, termPassword }.
 *   3. Open wss://<host><wsPath>.
 *   4. As soon as the socket opens, send "<pveUser>:<termPassword>\n" — this
 *      is how Proxmox termproxy authenticates the client side. After that,
 *      both directions carry raw UTF-8 terminal bytes.
 *   5. Wire xterm.js's `data` event to socket.send (user keystrokes) and the
 *      socket's `message` event to terminal.write (output from the VM).
 *
 * We deliberately do NOT use noVNC here — Ubuntu cloud images don't expose
 * a framebuffer, so noVNC paints blank. agetty already runs on ttyS0 in
 * every cloud image, so the serial console works for every distro we ship.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

interface SessionInfo {
  sessionId: string;
  vmid: number;
  hostname: string;
  wsPath: string;
  pveUser: string;
  termPassword: string;
  expiresAt: string;
}

const TermConsole: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [status, setStatus] = useState<string>('Connecting…');
  const [err, setErr] = useState<string>('');
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!sessionId || !containerRef.current) return;

    let disposed = false;
    (async () => {
      try {
        // 1. session config — auth-gated: the endpoint returns the termproxy
        // ticket, so it requires the logged-in owner's token, not just the URL.
        const token = localStorage.getItem('token');
        const resp = await fetch(`/api/vnc/session/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const j = await resp.json();
        if (disposed) return;
        if (!j.success) {
          setErr(j.error || 'Session not found or expired');
          return;
        }
        setInfo(j);

        // 2. spin up xterm
        const term = new Terminal({
          cursorBlink: true,
          fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Consolas, monospace',
          fontSize: 13,
          theme: {
            background: '#0f172a',  // slate-900, matches the dashboard glass-aurora dark canvas
            foreground: '#e2e8f0',  // slate-200
            cursor: '#fbbf24',      // amber-400
          },
          // termproxy speaks UTF-8 directly; xterm defaults to UTF-8.
          allowProposedApi: true,
          scrollback: 5000,
        });
        const fit = new FitAddon();
        term.loadAddon(fit);
        if (!containerRef.current) return;
        term.open(containerRef.current);
        fit.fit();
        termRef.current = term;
        fitRef.current = fit;

        // 3. open WS to our proxy
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const ws = new WebSocket(`${wsProtocol}//${window.location.host}${j.wsPath}`);
        // Proxmox sends binary frames, xterm wants strings. arraybuffer +
        // TextDecoder on the read side is the safest path for non-ASCII.
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;
        const decoder = new TextDecoder('utf-8', { fatal: false });
        const encoder = new TextEncoder();

        // Proxmox termproxy framing (matches PVE's stock xterm.js):
        //   - Auth frame (first):     "<user>:<ticket>\n"
        //   - Input keystrokes:       "0:<byte-length>:<utf8 bytes>"
        //   - Resize control:         "1:<cols>:<rows>:"
        //   - Pong (when asked "2"):  "2"
        // The server sends raw output bytes back (no framing).
        // Confirmed by sniffing the WS: raw byte sends did not produce any
        // server response after the auth banner, but framed sends do.
        const sendInput = (data: string) => {
          try {
            const len = encoder.encode(data).length;
            ws.send(`0:${len}:${data}`);
          } catch { /* */ }
        };
        const sendResize = (cols: number, rows: number) => {
          try { ws.send(`1:${cols}:${rows}:`); } catch { /* */ }
        };

        // Proxmox termproxy is stateful: the server only starts piping serial
        // bytes after it sees our auth frame AND replies "OK". If we wire up
        // term.onData / send a resize before that "OK" arrives, those frames
        // race the handshake and the session ends up authenticated but mute.
        // Gate everything post-auth on the first server message.
        let authed = false;

        ws.addEventListener('open', () => {
          ws.send(`${j.pveUser}:${j.termPassword}\n`);
          setStatus('Authenticating…');
        });

        ws.addEventListener('message', (ev: MessageEvent) => {
          // Server output is raw terminal bytes. Two out-of-band frames matter:
          //  - "OK"  — termproxy auth success; only after this can we send keys
          //  - "2"   — ping; reply "2" to keep the 30s timer fresh
          let text = '';
          if (typeof ev.data === 'string') {
            text = ev.data;
          } else if (ev.data instanceof ArrayBuffer) {
            text = decoder.decode(ev.data);
          } else if (ev.data instanceof Blob) {
            ev.data.arrayBuffer().then(buf => term.write(decoder.decode(buf))).catch(() => {/**/});
            return;
          }
          if (!authed && text === 'OK') {
            authed = true;
            setStatus('Connected');
            setConnected(true);
            sendResize(term.cols, term.rows);
            // The VM was booted before the customer ever opened a console,
            // so agetty --autologin already ran and the shell already printed
            // its prompt to a serial line nobody was reading. A bare \r
            // doesn't repaint that prompt — the shell only echoes a new one
            // when input is terminated by \n. Send \n once so the user lands
            // on a visible prompt instead of an empty screen.
            sendInput('\n');
            term.onData((data: string) => { if (authed) sendInput(data); });
            term.onResize(({ cols, rows }: { cols: number; rows: number }) => {
              if (authed) sendResize(cols, rows);
            });
            // xterm renders into a child <textarea>; without an explicit
            // focus the iframe needs a click before keystrokes are captured.
            try { term.focus(); } catch { /* */ }
            return;
          }
          if (text === '2') {
            try { ws.send('2'); } catch { /* */ }
            return;
          }
          term.write(text);
        });

        ws.addEventListener('close', (ev: CloseEvent) => {
          setConnected(false);
          if (ev.code === 1000) {
            setStatus('Disconnected');
          } else {
            setStatus('Connection lost');
            setErr(`Console connection closed (code ${ev.code}). Click Console again from the VPS panel for a fresh session.`);
          }
        });
        ws.addEventListener('error', () => {
          setStatus('Connection error');
          setErr('Websocket error — the console session may have expired.');
        });

        // Refit when the viewport changes.
        const onResize = () => { try { fit.fit(); } catch { /* */ } };
        window.addEventListener('resize', onResize);
        // Also store the listener so we can remove on cleanup.
        (term as any)._12and22_onResize = onResize;
      } catch (e: any) {
        if (disposed) return;
        setErr(e?.message || 'Failed to start console');
      }
    })();

    return () => {
      disposed = true;
      if (termRef.current && (termRef.current as any)._12and22_onResize) {
        window.removeEventListener('resize', (termRef.current as any)._12and22_onResize);
      }
      try { wsRef.current?.close(); } catch { /* */ }
      try { termRef.current?.dispose(); } catch { /* */ }
      wsRef.current = null;
      termRef.current = null;
      fitRef.current = null;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Top bar */}
      <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="font-semibold">VPS Console</div>
          {info && (
            <div className="text-xs text-gray-400">
              {info.hostname} (vmid {info.vmid})
            </div>
          )}
          <div className={`text-xs px-2 py-0.5 rounded ${
            connected ? 'bg-green-700 text-green-100' :
            err ? 'bg-red-700 text-red-100' :
            'bg-amber-700 text-amber-100'
          }`}>
            {connected ? 'Connected' : err ? 'Error' : status}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.close()}
            className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded"
          >
            Close
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-red-900 text-red-100 px-4 py-2 text-sm border-b border-red-800">
          {err}
        </div>
      )}

      {/* Terminal canvas */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden"
        style={{ background: '#0f172a', padding: '8px' }}
      />

      <div className="bg-gray-800 text-gray-400 text-xs px-4 py-1.5 flex-shrink-0 border-t border-gray-700">
        Session expires in 5 minutes. Keystrokes go directly to the VM serial console.
        Log in as <code className="text-amber-300">ubuntu</code>.
      </div>
    </div>
  );
};

export default TermConsole;
